import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, Package, AlertTriangle, Search, Download, ChevronDown, ChevronRight, Calendar, Clock, Skull, Archive } from 'lucide-react';
import { stockAgeingAPI } from '../../services/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const BUCKET_CONFIG = [
  { key: 'bucket_0_30', qkey: 'bucket_0_30_qty', label: '0-30 days', short: '0-30d', color: '#059669', bar: 'bg-emerald-500' },
  { key: 'bucket_31_60', qkey: 'bucket_31_60_qty', label: '31-60 days', short: '31-60d', color: '#d97706', bar: 'bg-amber-500' },
  { key: 'bucket_61_90', qkey: 'bucket_61_90_qty', label: '61-90 days', short: '61-90d', color: '#ea580c', bar: 'bg-orange-500' },
  { key: 'bucket_90_plus', qkey: 'bucket_90_plus_qty', label: '90+ days', short: '90+d', color: '#dc2626', bar: 'bg-red-500' },
];

const fmtINR = (v) => {
  if (!v || v === 0) return '-';
  if (v >= 10000000) return `${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(2)} L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)} K`;
  return v.toLocaleString('en-IN');
};

const fmtFull = (v) => (!v || v === 0) ? '-' : v.toLocaleString('en-IN', { maximumFractionDigits: 2 });

const StockAgeingReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [view, setView] = useState('products'); // products or batches
  const [bucketFilter, setBucketFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => { loadReport(); }, []);

  const loadReport = async () => {
    setLoading(true);
    try { setData(await stockAgeingAPI.getReport()); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const categories = useMemo(() => {
    if (!data) return [];
    return Object.keys(data.summary.categories || {}).sort();
  }, [data]);

  const filteredProducts = useMemo(() => {
    if (!data) return [];
    let rows = [...data.products];
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(r => r.product_name.toLowerCase().includes(q) || (r.category || '').toLowerCase().includes(q));
    }
    if (catFilter !== 'all') rows = rows.filter(r => r.category === catFilter);
    if (bucketFilter === 'dead') rows = rows.filter(r => r.bucket_90_plus > 0);
    else if (bucketFilter === 'old') rows = rows.filter(r => r.bucket_61_90 > 0 || r.bucket_90_plus > 0);
    else if (bucketFilter === 'expiry') rows = rows.filter(r => r.has_expiry_risk);
    return rows;
  }, [data, search, catFilter, bucketFilter]);

  const filteredItems = useMemo(() => {
    if (!data) return [];
    let rows = [...data.items];
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(r => r.product_name.toLowerCase().includes(q) || (r.batch || '').toLowerCase().includes(q) || (r.category || '').toLowerCase().includes(q));
    }
    if (catFilter !== 'all') rows = rows.filter(r => r.category === catFilter);
    if (bucketFilter === 'dead') rows = rows.filter(r => r.is_dead_stock);
    else if (bucketFilter === 'old') rows = rows.filter(r => r.age_days > 60);
    else if (bucketFilter === 'expiry') rows = rows.filter(r => r.expiry_status);
    return rows;
  }, [data, search, catFilter, bucketFilter]);

  const toggleExpand = (key) => {
    setExpandedRows(prev => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };

  const exportToExcel = () => {
    if (!data) return;
    const prodRows = filteredProducts.map(p => ({
      'Product': p.product_name, 'Category': p.category, 'Unit': p.unit,
      'Total Qty': p.total_qty, 'Total Value': p.total_value, 'Batches': p.batch_count,
      '0-30 Days (₹)': p.bucket_0_30, '31-60 Days (₹)': p.bucket_31_60,
      '61-90 Days (₹)': p.bucket_61_90, '90+ Days (₹)': p.bucket_90_plus,
      'Oldest (Days)': p.oldest_days, 'Expiry Risk': p.has_expiry_risk ? 'YES' : '',
    }));
    const batchRows = filteredItems.map(i => ({
      'Product': i.product_name, 'Category': i.category, 'Batch': i.batch,
      'Branch': i.branch, 'Qty': i.quantity, 'Unit': i.unit, 'Value': i.value,
      'Inward Date': i.inward_date, 'Age (Days)': i.age_days, 'Bucket': i.bucket,
      'Mfg Date': i.mfg_date, 'Expiry Date': i.expiry_date,
      'Expiry Status': i.expiry_status || '', 'Dead Stock': i.is_dead_stock ? 'YES' : '',
    }));
    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(prodRows); ws1['!cols'] = Array(12).fill({ wch: 16 });
    XLSX.utils.book_append_sheet(wb, ws1, 'Product Summary');
    const ws2 = XLSX.utils.json_to_sheet(batchRows); ws2['!cols'] = Array(14).fill({ wch: 16 });
    XLSX.utils.book_append_sheet(wb, ws2, 'Batch Detail');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Stock_Ageing_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600" /><span className="ml-2 text-sm text-gray-500">Loading stock ageing...</span>
    </div>
  );
  if (!data) return <p className="text-sm text-gray-500 text-center py-16">No data</p>;

  const { summary: s } = data;
  const maxBucket = Math.max(s.bucket_0_30 || 0, s.bucket_31_60 || 0, s.bucket_61_90 || 0, s.bucket_90_plus || 0, 1);

  return (
    <div data-testid="stock-ageing-report" className="space-y-4">
      {/* Date */}
      <div className="flex items-center gap-2 text-[11px] text-gray-500">
        <Calendar className="w-3.5 h-3.5" />Report as on: <span className="font-semibold text-gray-700">{s.report_date ? new Date(s.report_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Today'}</span>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#24549c15' }}>
            <Package className="w-4 h-4" style={{ color: '#24549c' }} />
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }} data-testid="total-stock-value">{fmtINR(s.total_value)}</p>
            <p className="text-[10px] text-gray-500">Total Stock Value</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-red-100 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-red-50">
            <Skull className="w-4 h-4 text-red-500" />
          </div>
          <div>
            <p className="text-lg font-bold text-red-600" style={{ fontFamily: 'DM Sans' }} data-testid="dead-stock-value">{fmtINR(s.dead_stock_value)}</p>
            <p className="text-[10px] text-gray-500">Dead Stock (90+d)</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-sm font-bold text-gray-900">{s.total_items}</p>
          <p className="text-[10px] text-gray-500">Total Batches</p>
        </div>
        {BUCKET_CONFIG.map(b => (
          <div key={b.key} className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-sm font-bold" style={{ color: b.color }}>{fmtINR(s[b.key] || 0)}</p>
            <p className="text-[10px] text-gray-500">{b.label}</p>
            <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${b.bar}`} style={{ width: `${Math.max(2, ((s[b.key] || 0) / maxBucket) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>

      {/* Expiry Alerts */}
      {(s.expired > 0 || s.expiring_30 > 0) && (
        <div className="p-3 rounded-xl border border-red-200 bg-red-50/50 flex items-start gap-3" data-testid="expiry-alert">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-red-800">Expiry Alert</p>
            <p className="text-[10px] text-red-600 mt-0.5">
              {s.expired > 0 && <span className="font-bold">{s.expired} expired</span>}
              {s.expired > 0 && s.expiring_30 > 0 && ' | '}
              {s.expiring_30 > 0 && <span>{s.expiring_30} expiring within 30 days</span>}
              {s.expiring_60 > 0 && <span> | {s.expiring_60} within 60 days</span>}
            </p>
          </div>
        </div>
      )}

      {/* Dead Stock Alert */}
      {s.dead_stock_items > 0 && (
        <div className="p-3 rounded-xl border border-amber-200 bg-amber-50/50 flex items-start gap-3" data-testid="dead-stock-alert">
          <Archive className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-amber-800">{s.dead_stock_items} batch{s.dead_stock_items > 1 ? 'es' : ''} classified as dead stock (90+ days)</p>
            <p className="text-[10px] text-amber-600 mt-0.5">Value: INR {fmtFull(s.dead_stock_value)} — consider write-off, discounting, or consumption planning</p>
          </div>
        </div>
      )}

      {/* Ageing Bar */}
      {s.total_value > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="text-xs font-bold text-gray-900 mb-3">Inventory Ageing Distribution</h3>
          <div className="flex h-8 rounded-lg overflow-hidden">
            {BUCKET_CONFIG.map(b => {
              const pct = ((s[b.key] || 0) / s.total_value) * 100;
              if (pct < 0.5) return null;
              return (
                <div key={b.key} className={`${b.bar} flex items-center justify-center transition-all`}
                  style={{ width: `${pct}%` }}
                  title={`${b.label}: ₹${fmtFull(s[b.key])} (${pct.toFixed(1)}%)`}>
                  {pct > 8 && <span className="text-[10px] font-semibold text-white truncate px-1">{b.short} {pct.toFixed(0)}%</span>}
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-2.5">
            {BUCKET_CONFIG.map(b => (
              <div key={b.key} className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-sm ${b.bar}`} />
                <span className="text-[10px] text-gray-500">{b.label}: <span className="font-semibold text-gray-700">{fmtINR(s[b.key] || 0)}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {Object.keys(s.categories || {}).length > 1 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="text-xs font-bold text-gray-900 mb-3">By Category</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {Object.entries(s.categories).sort((a, b) => b[1].value - a[1].value).map(([cat, d]) => (
              <div key={cat} className="p-2.5 rounded-lg bg-gray-50 border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => setCatFilter(catFilter === cat ? 'all' : cat)}>
                <p className={`text-[10px] font-bold truncate ${catFilter === cat ? 'text-blue-700' : 'text-gray-700'}`}>{cat}</p>
                <p className="text-xs font-bold text-gray-900 mt-0.5">{fmtINR(d.value)}</p>
                <p className="text-[9px] text-gray-400">{d.items} batches</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input data-testid="stock-age-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search product, batch, category..." className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
        <div className="flex gap-1.5">
          {[
            { id: 'all', label: 'All' },
            { id: 'dead', label: 'Dead Stock (90+d)' },
            { id: 'old', label: 'Old (60+d)' },
            { id: 'expiry', label: 'Expiry Risk' },
          ].map(f => (
            <button key={f.id} onClick={() => setBucketFilter(f.id)} data-testid={`stock-age-filter-${f.id}`}
              className={`px-3 py-1.5 text-[11px] font-medium rounded-full transition-all ${
                bucketFilter === f.id
                  ? f.id === 'dead' || f.id === 'expiry' ? 'text-white bg-red-600' : 'text-white'
                  : f.id === 'dead' || f.id === 'expiry' ? 'text-red-600 bg-red-50 hover:bg-red-100' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
              }`} style={bucketFilter === f.id && f.id !== 'dead' && f.id !== 'expiry' ? { backgroundColor: '#24549c' } : {}}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5">
          <button onClick={() => setView('products')} data-testid="stock-age-view-products"
            className={`px-3 py-1.5 text-[11px] font-medium rounded-full ${view === 'products' ? 'text-white' : 'text-gray-600 bg-gray-100'}`}
            style={view === 'products' ? { backgroundColor: '#24549c' } : {}}>Product View</button>
          <button onClick={() => setView('batches')} data-testid="stock-age-view-batches"
            className={`px-3 py-1.5 text-[11px] font-medium rounded-full ${view === 'batches' ? 'text-white' : 'text-gray-600 bg-gray-100'}`}
            style={view === 'batches' ? { backgroundColor: '#24549c' } : {}}>Batch View</button>
        </div>
        <button onClick={exportToExcel} data-testid="export-stock-ageing"
          className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: '#0d6b3e' }}>
          <Download className="w-3.5 h-3.5" />Excel
        </button>
      </div>

      {/* Product View */}
      {view === 'products' && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="stock-age-product-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="w-8 px-2 py-2.5"></th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Product</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Qty</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Value</th>
                  {BUCKET_CONFIG.map(b => (
                    <th key={b.key} className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase" style={{ color: b.color }}>{b.short}</th>
                  ))}
                  <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Oldest</th>
                  <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Risk</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.length === 0 ? (
                  <tr><td colSpan="9" className="text-center py-8 text-sm text-gray-400">No items match filters</td></tr>
                ) : filteredProducts.map((p, idx) => {
                  const isExp = expandedRows.has(p.product_name);
                  const batches = data.items.filter(i => i.product_name === p.product_name);
                  return (
                    <React.Fragment key={idx}>
                      <tr className={`border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer ${p.bucket_90_plus > 0 ? 'bg-red-50/20' : ''}`}
                        onClick={() => toggleExpand(p.product_name)} data-testid={`stock-age-row-${idx}`}>
                        <td className="px-2 py-3 text-center">{isExp ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 inline" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 inline" />}</td>
                        <td className="px-4 py-3">
                          <p className="text-xs font-medium text-gray-900">{p.product_name}</p>
                          <p className="text-[10px] text-gray-400">{p.category} | {p.batch_count} batch{p.batch_count > 1 ? 'es' : ''}</p>
                        </td>
                        <td className="px-3 py-3 text-right text-xs font-semibold text-gray-700">{fmtFull(p.total_qty)} <span className="text-[9px] text-gray-400">{p.unit}</span></td>
                        <td className="px-3 py-3 text-right text-xs font-bold text-gray-900">{fmtINR(p.total_value)}</td>
                        {BUCKET_CONFIG.map(b => (
                          <td key={b.key} className="px-3 py-3 text-right">
                            <span className={`text-xs font-semibold ${p[b.key] > 0 ? '' : 'text-gray-300'}`} style={p[b.key] > 0 ? { color: b.color } : {}}>
                              {p[b.key] > 0 ? fmtINR(p[b.key]) : '-'}
                            </span>
                          </td>
                        ))}
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                            p.oldest_days > 90 ? 'bg-red-100 text-red-700' : p.oldest_days > 60 ? 'bg-orange-100 text-orange-700' :
                            p.oldest_days > 30 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                          }`}>{p.oldest_days}d</span>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {p.has_expiry_risk ? <AlertTriangle className="w-3.5 h-3.5 text-red-500 inline" /> : <span className="text-[10px] text-gray-300">-</span>}
                        </td>
                      </tr>
                      {isExp && batches.length > 0 && (
                        <tr><td colSpan="9" className="px-0 py-0">
                          <div className="bg-gray-50/70 border-t border-b border-gray-100">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-gray-200">
                                  <th className="text-left px-6 py-2 text-[9px] font-semibold text-gray-400 uppercase">Batch</th>
                                  <th className="text-left px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase">Branch</th>
                                  <th className="text-left px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase">Inward</th>
                                  <th className="text-right px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase">Qty</th>
                                  <th className="text-right px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase">Value</th>
                                  <th className="text-center px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase">Age</th>
                                  <th className="text-center px-4 py-2 text-[9px] font-semibold text-gray-400 uppercase">Expiry</th>
                                </tr>
                              </thead>
                              <tbody>
                                {batches.map((b, bi) => (
                                  <tr key={bi} className="border-b border-gray-100 last:border-0">
                                    <td className="px-6 py-2 text-[11px] font-medium text-blue-700 font-mono">{b.batch || '-'}</td>
                                    <td className="px-4 py-2 text-[11px] text-gray-600">{b.branch || '-'}</td>
                                    <td className="px-4 py-2 text-[11px] text-gray-600">{b.inward_date ? new Date(b.inward_date + 'T00:00:00').toLocaleDateString('en-IN') : '-'}</td>
                                    <td className="px-4 py-2 text-[11px] text-gray-700 text-right">{fmtFull(b.quantity)} {b.unit}</td>
                                    <td className="px-4 py-2 text-[11px] font-bold text-right text-gray-900">{fmtFull(b.value)}</td>
                                    <td className="px-4 py-2 text-center">
                                      <span className={`text-[10px] font-semibold ${b.age_days > 90 ? 'text-red-600' : b.age_days > 60 ? 'text-orange-600' : b.age_days > 30 ? 'text-amber-600' : 'text-gray-600'}`}>{b.age_days}d</span>
                                    </td>
                                    <td className="px-4 py-2 text-center">
                                      {b.expiry_status ? (
                                        <span className={`inline-flex px-2 py-0.5 text-[9px] font-bold rounded-full ${
                                          b.expiry_status === 'EXPIRED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                                        }`}>{b.expiry_status === 'EXPIRED' ? 'EXPIRED' : `${b.days_to_expiry}d left`}</span>
                                      ) : b.expiry_date ? (
                                        <span className="text-[10px] text-gray-500">{new Date(b.expiry_date + 'T00:00:00').toLocaleDateString('en-IN')}</span>
                                      ) : <span className="text-[10px] text-gray-300">-</span>}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t border-gray-100 text-[10px] text-gray-500">
            {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} | Total: INR {fmtFull(filteredProducts.reduce((s, p) => s + p.total_value, 0))}
          </div>
        </div>
      )}

      {/* Batch View */}
      {view === 'batches' && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="stock-age-batch-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Product</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Batch</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Branch</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Inward</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Qty</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Value</th>
                  <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Age</th>
                  <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Bucket</th>
                  <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Expiry</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr><td colSpan="9" className="text-center py-8 text-sm text-gray-400">No items match filters</td></tr>
                ) : filteredItems.map((i, idx) => (
                  <tr key={idx} className={`border-b border-gray-50 hover:bg-gray-50/50 ${i.is_dead_stock ? 'bg-red-50/20' : idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                    <td className="px-4 py-2.5">
                      <p className="text-xs font-medium text-gray-900">{i.product_name}</p>
                      <p className="text-[9px] text-gray-400">{i.category}</p>
                    </td>
                    <td className="px-3 py-2.5 text-[11px] font-mono text-blue-700">{i.batch || '-'}</td>
                    <td className="px-3 py-2.5 text-[11px] text-gray-600">{i.branch || '-'}</td>
                    <td className="px-3 py-2.5 text-[11px] text-gray-600">{i.inward_date ? new Date(i.inward_date + 'T00:00:00').toLocaleDateString('en-IN') : '-'}</td>
                    <td className="px-3 py-2.5 text-xs text-right font-semibold text-gray-700">{fmtFull(i.quantity)} <span className="text-[9px] text-gray-400">{i.unit}</span></td>
                    <td className="px-3 py-2.5 text-xs text-right font-bold text-gray-900">{fmtFull(i.value)}</td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`text-[10px] font-bold ${i.age_days > 90 ? 'text-red-600' : i.age_days > 60 ? 'text-orange-600' : i.age_days > 30 ? 'text-amber-600' : 'text-emerald-600'}`}>{i.age_days}d</span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                        i.bucket === '90+' ? 'bg-red-100 text-red-700' : i.bucket === '61-90' ? 'bg-orange-100 text-orange-700' :
                        i.bucket === '31-60' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                      }`}>{i.bucket}</span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {i.expiry_status ? (
                        <span className={`inline-flex px-2 py-0.5 text-[9px] font-bold rounded-full ${
                          i.expiry_status === 'EXPIRED' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}>{i.expiry_status === 'EXPIRED' ? 'EXPIRED' : `${i.days_to_expiry}d`}</span>
                      ) : <span className="text-[10px] text-gray-300">-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t border-gray-100 text-[10px] text-gray-500">
            {filteredItems.length} batch{filteredItems.length !== 1 ? 'es' : ''} | Total: INR {fmtFull(filteredItems.reduce((s, i) => s + i.value, 0))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StockAgeingReport;
